import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Icon from "@/src/lib/components/custom/Icon";
import { Image } from "@/src/lib/components/custom/Image";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/src/lib/components/ui/alert-dialog";
import { Badge } from "@/src/lib/components/ui/badge";
import { Button } from "@/src/lib/components/ui/button";
import { Card } from "@/src/lib/components/ui/card";
import { useApi } from "@/src/lib/hooks/api/use-api";
import type { GetTypes } from "@/src/lib/types/api-types";
import { cn } from "@/src/lib/utils";

// Helper to extract the inner Order type from the list response
type OrdersListResponse = GetTypes["orders"]["getByUserId"];
type OrderFromList = OrdersListResponse extends { orders?: (infer O)[] }
	? O
	: never;

interface OrderCardProps {
	order: OrderFromList;
	formatDate: (dateString: string) => string;
	index: number;
}

const getStatusColor = (status: string) => {
	switch (status) {
		case "received":
			return "bg-destructive/10 text-destructive border-destructive/10";
		case "confirmed":
			return "bg-chart-3/10 text-chart-3 border-chart-3/10";
		case "preparing":
			return "bg-orange-500/10 text-orange-600 border-orange-100";
		case "ready":
			return "bg-chart-2/10 text-chart-2 border-chart-2/10";
		case "out-for-delivery":
			return "bg-chart-4/10 text-chart-4 border-chart-4/10";
		case "delivered":
			return "bg-chart-2/10 text-chart-2 border-chart-2/10";
		case "cancelled":
			return "bg-red-500/10 text-red-600 border-red-100";
		default:
			return "bg-muted text-muted-foreground border-muted";
	}
};

const getStatusText = (status: string, t: (key: string) => string) => {
	switch (status) {
		case "received":
			return t("orders.card.status.received");
		case "confirmed":
			return t("orders.card.status.confirmed");
		case "preparing":
			return t("orders.card.status.preparing");
		case "ready":
			return t("orders.card.status.ready");
		case "out-for-delivery":
			return t("orders.card.status.out_for_delivery");
		case "delivered":
			return t("orders.card.status.delivered");
		case "cancelled":
			return t("orders.card.status.cancelled");
		default:
			return status;
	}
};

export default function OrderCard({
	order,
	formatDate,
	index,
}: OrderCardProps) {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const { orders } = useApi();
	const cancelOrder = orders.cancel;

	// Fetch full order details including items on component load
	const {
		data: expandedOrderData,
		isLoading: isLoadingDetails,
		refetch: refetchExpandedOrder,
	} = orders.getById({ orderId: order.id.toString() });
	// expandedOrderData is already typed by React Query - no need to manually type it
	const expandedOrder = expandedOrderData;
	const items = expandedOrder?.order?.items || [];
	const hasFullDetails = !!expandedOrder;

	// Use expanded data when available, fallback to basic order data
	const displayOrder = {
		...order,
		...(expandedOrder?.order || {}),
	};

	const heroImage = items[0]?.menuItem?.imageUrl;
	const heroLabel = items[0]?.menuItem?.name || "Order";

	// Parse scheduled date safely
	const scheduledDate = displayOrder.scheduledFor
		? (() => {
				try {
					const date = new Date(displayOrder.scheduledFor);
					return Number.isNaN(date.getTime()) ? null : date;
				} catch {
					return null;
				}
			})()
		: null;

	const toggleExpanded = () => setIsExpanded(!isExpanded);
	const canCancelOrder = ["pending", "received", "preparing"].includes(
		displayOrder.status,
	);
	const cancellationWarning =
		displayOrder.status === "preparing"
			? t("orders.card.details.cancel_warning_fee")
			: t("orders.card.details.cancel_warning_void");

	const handleCancelDialogOpen = async () => {
		// Ensure we have the latest order state before showing the confirmation dialog.
		try {
			await refetchExpandedOrder();
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("orders.card.details.error");
			toast.error(message);
			return;
		}

		setCancelDialogOpen(true);
	};

	const handleCancelOrder = async () => {
		try {
			await cancelOrder.mutateAsync({ orderId: displayOrder.id.toString() });
			toast.success(t("orders.card.details.cancel_success"));
			setCancelDialogOpen(false);
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: t("orders.card.details.cancel_failed");
			toast.error(message);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.2, delay: index * 0.05, ease: "easeOut" }}
		>
			<Card className="overflow-hidden rounded-2xl border shadow-sm hover:shadow-md transition-all duration-200">
				{/* Header Row - More Spacious */}
				<button
					type="button"
					className="p-4 sm:p-5 md:p-6 w-full cursor-pointer hover:bg-muted/30 transition-colors outline-none focus-visible:bg-muted/40 text-left"
					onClick={toggleExpanded}
					aria-expanded={isExpanded}
				>
					<div className="flex items-start justify-between gap-3 sm:gap-4">
						<div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
							{/* Hero Image */}
							<div className="flex flex-col items-center gap-2">
								<div className="rounded-large bg-primary/5 text-primary shrink-0 border border-primary/15 size-14 sm:size-16 md:size-20 flex items-center justify-center overflow-hidden">
									{heroImage ? (
										<Image
											src={heroImage}
											alt={heroLabel}
											className="w-full h-full object-cover rounded-lg"
										/>
									) : (
										<Icon
											name="Package"
											className="w-5 h-5 sm:w-6 sm:h-6 md:size-7"
										/>
									)}
								</div>
								<span className="font-mono text-xs text-muted-foreground rounded-full bg-muted px-3 py-1">
									#{String(order.id).slice(0, 8)}
								</span>
							</div>

							{/* Main Content */}
							<div className="flex flex-col gap-1 sm:gap-1.5 min-w-0 flex-1">
								{/* Price - More Prominent */}
								<div className="flex items-baseline gap-2">
									<span className="font-bold text-lg sm:text-xl md:text-2xl leading-tight text-primary">
										€{Number(order.totalPrice).toFixed(2)}
									</span>
								</div>

								<div className="flex items-center gap-1.5 rounded-full text-muted-foreground py-1.5 text-xs">
									<Icon name="Calendar" className="w-3.5 h-3.5 shrink-0" />
									<span className="whitespace-nowrap font-medium">
										{formatDate(
											order.orderDate?.toString() ||
												order.createdAt?.toString() ||
												"",
										)}
									</span>
								</div>

								{/* Metadata Pills - More Spacious */}
								<div className="flex flex-wrap gap-2 text-xs">
									<Badge
										variant="outline"
										className={cn(
											"text-xs font-medium border rounded-full",
											getStatusColor(displayOrder.status),
										)}
									>
										{getStatusText(displayOrder.status, t)}
									</Badge>

									<Badge
										variant="outline"
										className={cn(
											"text-xs font-medium border rounded-full",
											displayOrder.paymentMethod === "cash"
												? "bg-amber-100 text-amber-800 border-amber-100"
												: "bg-blue-100 text-blue-800 border-blue-100",
										)}
									>
										<Icon
											name={
												displayOrder.paymentMethod === "cash"
													? "Banknote"
													: "CreditCard"
											}
											className="w-3.5 h-3.5 shrink-0"
										/>
										<span className="whitespace-nowrap">
											{displayOrder.paymentMethod === "cash"
												? t("orders.card.cash")
												: t("orders.card.card")}
										</span>
									</Badge>
								</div>
							</div>
						</div>

						{/* Status and Expand Button */}
						<div className="flex flex-col items-end gap-2 shrink-0">
							<span className="h-10 w-10 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors">
								<Icon
									name="ChevronDown"
									className={cn(
										"w-5 h-5 transition-transform duration-200",
										isExpanded && "rotate-180",
									)}
								/>
							</span>
						</div>
					</div>
				</button>

				{/* Expanded Details */}
				<AnimatePresence>
					{isExpanded && (
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: "auto", opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="overflow-hidden bg-muted/30 border-t"
						>
							<div className="p-4 sm:p-5 md:p-6 space-y-5 sm:space-y-6 cursor-default">
								{isLoadingDetails && !hasFullDetails ? (
									<div className="flex flex-col items-center justify-center py-8 sm:py-10">
										<Icon
											name="LoaderCircle"
											className="w-6 h-6 sm:w-7 sm:h-7 animate-spin text-muted-foreground mb-3"
										/>
										<p className="text-sm text-muted-foreground">
											{t("orders.card.details.loading")}
										</p>
									</div>
								) : hasFullDetails ? (
									<>
										{/* Order Items Section */}
										<div className="space-y-4">
											<div className="flex items-center gap-3">
												<Icon
													name="Utensils"
													className="w-5 h-5 text-primary"
												/>
												<div className="flex-1">
													<h3 className="font-semibold text-base sm:text-lg">
														{t("orders.card.details.items_header")}
													</h3>
													<p className="text-sm text-muted-foreground">
														{t(
															items.length === 1
																? "orders.card.items_count"
																: "orders.card.items_count_plural",
															{ count: items.length },
														)}
													</p>
												</div>
											</div>

											<div className="space-y-3">
												{items.map((item) => (
													<div
														key={item.id}
														className="flex items-start gap-3 sm:gap-4 p-4 bg-card rounded-xl border shadow-sm hover:shadow-md transition-shadow"
													>
														<div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 bg-muted rounded-xl overflow-hidden border">
															{item.menuItem?.imageUrl ? (
																<Image
																	src={item.menuItem.imageUrl}
																	alt={item.menuItem.name}
																	className="w-full h-full object-cover"
																/>
															) : (
																<div className="w-full h-full flex items-center justify-center">
																	<Icon
																		name="Utensils"
																		className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground"
																	/>
																</div>
															)}
														</div>

														<div className="flex-1 min-w-0 space-y-2">
															<div className="flex justify-between items-start gap-3">
																<h4 className="font-semibold text-sm sm:text-base leading-tight">
																	{item.menuItem?.name}
																</h4>
																<span className="font-bold text-base sm:text-lg text-primary shrink-0">
																	€{Number(item.totalPrice).toFixed(2)}
																</span>
															</div>

															<div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
																<Badge
																	variant="secondary"
																	className="font-medium px-2 py-0.5 text-xs"
																>
																	{item.menuSize?.sizeName}
																</Badge>
																<span className="text-muted-foreground/70">
																	×{item.quantity}
																</span>
																{item.extras && item.extras.length > 0 && (
																	<span className="truncate text-muted-foreground/70">
																		+{" "}
																		{item.extras
																			.map((e: { name: string }) => e.name)
																			.join(", ")}
																	</span>
																)}
															</div>
														</div>
													</div>
												))}
											</div>
										</div>

										{/* Delivery & Order Summary Grid */}
										<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
											{/* Delivery Information */}
											<div className="space-y-4">
												<div className="flex items-center gap-3">
													<Icon
														name="MapPin"
														className="w-5 h-5 text-primary"
													/>

													<div className="flex justify-between items-center gap-4 w-full">
														<h3 className="font-semibold text-base sm:text-lg">
															{t("confirmation.delivery.header")}
														</h3>

														{scheduledDate && (
															<span className="text-xs font-semibold text-primary bg-accent w-fit p-2 px-3 rounded-full flex items-center gap-2">
																<Icon
																	name="Clock"
																	className="w-3.5 h-3.5 shrink-0"
																/>
																{scheduledDate.toLocaleTimeString([], {
																	hour: "numeric",
																	minute: "2-digit",
																	hour12: true,
																})}
															</span>
														)}
													</div>
												</div>

												<div className="p-4 sm:p-5 rounded-xl border bg-card space-y-3">
													<div className="space-y-1">
														<p className="font-medium text-sm sm:text-base text-foreground">
															{expandedOrder?.order?.address?.streetAddress}
														</p>
														<p className="text-muted-foreground text-sm">
															{expandedOrder?.order?.address?.city},{" "}
															{expandedOrder?.order?.address?.state}{" "}
															{expandedOrder?.order?.address?.zipCode}
														</p>
														{expandedOrder?.order?.address?.phone && (
															<div className="flex items-center gap-2 pt-1">
																<Icon
																	name="Phone"
																	className="w-4 h-4 text-muted-foreground"
																/>
																<span className="text-sm text-muted-foreground">
																	{expandedOrder.order.address.phone}
																</span>
															</div>
														)}
													</div>
												</div>
											</div>

											{/* Order Summary & Actions */}
											<div className="space-y-4">
												<div className="flex items-center gap-3">
													<Icon
														name="Receipt"
														className="w-5 h-5 text-primary"
													/>
													<h3 className="font-semibold text-base sm:text-lg">
														{t("confirmation.summary.header")}
													</h3>
												</div>

												<div className="p-4 sm:p-5 rounded-xl border bg-card space-y-4">
													{/* Pricing Breakdown */}
													<div className="space-y-3">
														{(() => {
															const orderTotal =
																Number(expandedOrder?.order?.totalPrice) || 0;
															const deliveryFee = Number(
																expandedOrder?.order?.deliveryFee ?? 0,
															);
															const subtotal = orderTotal - deliveryFee;
															return (
																<>
																	<div className="flex justify-between text-sm text-muted-foreground">
																		<span>
																			{t("orders.card.details.subtotal")}
																		</span>
																		<span>€{subtotal.toFixed(2)}</span>
																	</div>
																	<div className="flex justify-between text-sm text-muted-foreground">
																		<span>
																			{t("orders.card.details.delivery")}
																		</span>
																		<span>
																			{deliveryFee === 0
																				? t("common.free")
																				: `€${deliveryFee.toFixed(2)}`}
																		</span>
																	</div>
																	<div className="flex justify-between font-bold text-lg pt-3 border-t border-border">
																		<span>
																			{t("orders.card.details.total")}
																		</span>
																		<span className="text-primary">
																			€
																			{Number(
																				expandedOrder?.order?.totalPrice,
																			).toFixed(2)}
																		</span>
																	</div>
																</>
															);
														})()}
													</div>

													{/* Action Buttons */}
													<div className="space-y-3 pt-2">
														{canCancelOrder && (
															<>
																<div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
																	{cancellationWarning}
																</div>
																<Button
																	variant="outline"
																	size="sm"
																	className="w-full h-10 text-sm font-medium text-destructive border-destructive/20 hover:bg-destructive/10"
																	onClick={handleCancelDialogOpen}
																	disabled={cancelOrder.isPending}
																>
																	{cancelOrder.isPending ? (
																		<>
																			<Icon
																				name="LoaderCircle"
																				className="w-4 h-4 mr-2 animate-spin"
																			/>
																			{t("orders.card.details.cancelling")}
																		</>
																	) : (
																		<>
																			<Icon name="X" className="w-4 h-4 mr-2" />
																			{t("orders.card.details.cancel_order")}
																		</>
																	)}
																</Button>
															</>
														)}
														{displayOrder.status === "cancelled" &&
															displayOrder.refundId && (
																<Button
																	variant="default"
																	size="sm"
																	className="w-full h-10 text-sm font-medium"
																	asChild
																>
																	<Link
																		to="/refund"
																		search={{ orderId: displayOrder.id }}
																	>
																		<Icon
																			name="ArrowRight"
																			className="w-4 h-4 mr-2"
																		/>
																		{t("orders.card.details.track_refund")}
																	</Link>
																</Button>
															)}
														<Button
															variant="outline"
															size="sm"
															className="w-full h-10 text-sm font-medium"
															asChild
														>
															<Link to="/support">
																<Icon
																	name="CircleHelp"
																	className="w-4 h-4 mr-2"
																/>
																{t("orders.card.details.get_help")}
															</Link>
														</Button>
													</div>
												</div>
											</div>
										</div>
									</>
								) : (
									<div className="text-center py-4 sm:py-6 text-sm sm:text-base text-muted-foreground">
										{t("orders.card.details.error")}
									</div>
								)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</Card>

			<AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("orders.card.details.cancel_order")}
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2">
								<p>{t("orders.card.details.cancel_confirm")}</p>
								<p className="text-amber-700">{cancellationWarning}</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleCancelOrder}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("orders.card.details.cancel_order")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</motion.div>
	);
}
